import { Component, EventEmitter, HostListener, Input, Output } from '@angular/core';

/**
 * Barra flutuante fixa no rodapé que aparece quando há ≥1 item selecionado
 * numa lista de cards (submissões/revisões). Mostra contagem, botão de
 * copiar e botão de limpar seleção. Estilizada no padrão JEMS3 (sombra
 * suave, retângulo arredondado, centralizada horizontalmente).
 *
 * Consumidor mantém o `Set<id>` de selecionados e decide o que copiar.
 */
@Component({
  selector: 'app-selection-actions-bar',
  templateUrl: './selection-actions-bar.component.html',
  styleUrls: ['./selection-actions-bar.component.scss'],
})
export class SelectionActionsBarComponent {
  @Input() count = 0;
  @Input() copyLabel = 'Copiar';
  @Input() copiedLabel = 'Copiado!';
  @Input() clearLabel = 'Limpar seleção';

  @Output() copyClick = new EventEmitter<void>();
  @Output() clearClick = new EventEmitter<void>();

  /** Hint do atalho — `⌘C` no Mac, `Ctrl+C` nos demais SOs. */
  readonly shortcutHint = this.detectMac() ? '⌘C' : 'Ctrl+C';

  /** Feedback visual local — o botão troca pra "Copiado!" + ícone check
   *  por 1,5s após cada copy. Tudo dentro de `position:fixed` da barra,
   *  então NÃO causa shift de layout na página (vs `MatSnackBar` que
   *  adicionava `cdk-overlay-container` ao body). */
  copied = false;
  private copiedTimeoutId: any = null;

  /** Cmd/Ctrl+C dispara o copy QUANDO há seleção e o foco NÃO está em
   *  campo de texto editável (textarea/contenteditable/input de texto) e
   *  não há texto realmente selecionado.
   *
   *  IMPORTANTE: `<input type="checkbox">` NÃO conta como campo de texto
   *  — quando o user marca o checkbox de seleção, o activeElement passa
   *  a ser ele, mas Ctrl+C nesse contexto DEVE disparar o copy. Por isso
   *  filtra por `input.type` (igual ao chair em
   *  `submissions-list-table-v2.component.ts:1509`). */
  @HostListener('document:keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    if (this.count <= 0) { return; }
    const isCopy = (event.ctrlKey || event.metaKey) && (event.key === 'c' || event.key === 'C');
    if (!isCopy) { return; }
    const ae = document.activeElement as HTMLInputElement | null;
    const nonText = ['checkbox', 'radio', 'button', 'submit', 'range', 'color', 'file'];
    const isTextField = !!ae && (
      ae.tagName === 'TEXTAREA' ||
      (ae as HTMLElement).isContentEditable ||
      (ae.tagName === 'INPUT' && !nonText.includes((ae.type || '').toLowerCase()))
    );
    if (isTextField) { return; }
    const sel = window.getSelection();
    if (sel && sel.toString().length > 0) { return; }
    event.preventDefault();
    this.preserveScrollAndCopy();
  }

  /** Chamado pelo handler do botão (click) — passa pelo mesmo wrapper de
   *  preservação de scroll que o atalho. */
  onCopyButtonClick(): void {
    this.preserveScrollAndCopy();
  }

  /** Captura a posição de scroll ANTES de emitir copy e restaura DEPOIS.
   *  Necessário porque:
   *   1. O fallback do clipboard (textarea+execCommand) chama `focus()` —
   *      mesmo com `{preventScroll:true}`, alguns browsers ignoram.
   *   2. O `MatSnackBar` da notificação adiciona o cdk-overlay-container
   *      ao body, e em alguns browsers/temas a barra de scrollbar do
   *      window aparece/some momentaneamente — a página dá um "salto"
   *      lateral.
   *   3. Side-effects de qualquer focus/blur do clipboard API em browsers
   *      mais novos.
   *  O wrapper é uma defesa universal — não importa qual desses dispare. */
  private preserveScrollAndCopy(): void {
    const x = window.scrollX;
    const y = window.scrollY;
    const root = document.documentElement;
    const rootScrollTop = root.scrollTop;
    const rootScrollLeft = root.scrollLeft;
    this.copyClick.emit();
    this.flashCopied();
    const restore = () => {
      if (window.scrollX !== x || window.scrollY !== y) {
        window.scrollTo({ left: x, top: y, behavior: 'auto' });
      }
      if (root.scrollTop !== rootScrollTop || root.scrollLeft !== rootScrollLeft) {
        root.scrollTop = rootScrollTop;
        root.scrollLeft = rootScrollLeft;
      }
    };
    requestAnimationFrame(restore);
    setTimeout(restore, 100);
  }

  /** Mostra "Copiado!" + ícone check no botão por 1,5s. */
  private flashCopied(): void {
    this.copied = true;
    if (this.copiedTimeoutId) { clearTimeout(this.copiedTimeoutId); }
    this.copiedTimeoutId = setTimeout(() => {
      this.copied = false;
      this.copiedTimeoutId = null;
    }, 1000);
  }

  private detectMac(): boolean {
    return typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);
  }
}
